import React from "react";
import PropTypes from "prop-types";

// @mui material components
import Menu from "@mui/material/Menu";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

function TicketsMenu({ anchorEl, open, onClose, tickets, onOpenModal, darkMode }) {
  const { t, i18n } = useTranslation();

  // Helper to color-code status
  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
        return "info";
      case "IN_PROGRESS":
        return "warning";
      case "RESOLVED":
        return "success";
      case "REJECTED":
        return "error";
      default:
        return "secondary";
    }
  };

  const formatDateByLang = (date, lang) =>
    new Date(date).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US");

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(open)}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{
        sx: {
          mt: 1.5,
          borderRadius: "20px",
          width: "350px",
          maxHeight: "500px",
          boxShadow: "0 15px 50px rgba(0,0,0,0.15)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <MDBox
        px={2.5}
        py={2}
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap={1}
        backgroundColor={darkMode ? "rgba(255,255,255,0.03)" : "#fbfbfb"}
      >
        <MDTypography
          variant="h6"
          fontWeight="bold"
          sx={{ fontSize: "1rem", textAlign: "center", width: "100%" }}
        >
          {t("tickets.title")}
        </MDTypography>

        <MDButton
          variant="gradient"
          color="info"
          size="small"
          startIcon={<Icon>add</Icon>}
          onClick={() => {
            onClose();
            onOpenModal();
          }}
        >
          {t("tickets.create")}
        </MDButton>
      </MDBox>
      <Divider sx={{ m: 0 }} />

      {/* Ticket List */}
      <MDBox sx={{ overflowY: "auto", maxHeight: "400px", py: 1 }}>
        {!tickets || tickets.length === 0 ? (
          <MDBox py={4} textAlign="center">
            <Icon sx={{ fontSize: "40px !important", color: "grey.300", mb: 1 }}>
              confirmation_number
            </Icon>
            <MDTypography variant="button" display="block" color="text">
              {t("tickets.noTickets")}
            </MDTypography>
          </MDBox>
        ) : (
          tickets.map((ticket, index) => (
            <Box key={ticket.id || index} sx={{ px: 2, py: 1.5, cursor: "default" }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="start" mb={0.5}>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  textTransform="capitalize"
                  sx={{ maxWidth: "70%" }}
                >
                  {ticket.subject}
                </MDTypography>
                <MDBadge
                  badgeContent={t(`tickets.${ticket.status}`)}
                  color={getStatusColor(ticket.status)}
                  variant="gradient"
                  size="xs"
                  container
                />
              </MDBox>

              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="caption" color="text">
                  {ticket.type === "BUG" ? `🐞 ${t("tickets.bug")}` : `💡 ${t("tickets.feature")}`}
                </MDTypography>
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  {formatDateByLang(ticket.createdAt, i18n.language)}
                </MDTypography>
              </MDBox>

              {index !== tickets.length - 1 && <Divider sx={{ mt: 2, mb: 0, opacity: 0.5 }} />}
            </Box>
          ))
        )}
      </MDBox>
    </Menu>
  );
}

TicketsMenu.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tickets: PropTypes.array.isRequired,
  onOpenModal: PropTypes.func.isRequired,
  darkMode: PropTypes.bool,
};

export default TicketsMenu;
